import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { NewReportData } from "../types/newReportTypes";
import { Theme } from "../../../providers/ThemeProvider";
import useTheme from "../../../common/hooks/useTheme";
import { GetNewReport } from "../../../common/network/Api";
import { useAppStore } from "../../../providers/AppStore";
import GeneratePDF from "../../reports/components/UI/GeneratePDF";
import ScaledNewReportPages from "../../reports/components/ScaledNewReportPages";
import classes from "./NewReport.module.css";

const NewReport = () => {
  const theme: Theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<NewReportData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingPDF, setIsCreatingPDF] = useState(false);
  const { ASGetLogicCategoriesList, ASGetBiomarkersList } = useAppStore();

  // Load logic categories and biomarkers data on mount (for field label formatting)
  useEffect(() => {
    const loadCategoryData = async () => {
      try {
        // Load logic categories (populates Pnoe Metrics and Lab Work in Redux)
        await Promise.all([ASGetLogicCategoriesList(), ASGetBiomarkersList()]);
      } catch (err) {
        console.error("Error loading category data:", err);
        // Don't block report loading if category data fails
      }
    };

    loadCategoryData();
  }, [ASGetLogicCategoriesList, ASGetBiomarkersList]);

  useEffect(() => {
    const fetchReportData = async () => {
      if (!id) {
        setError("Report ID is required");
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await GetNewReport(id);

        if (response) {
          setData(response);
        } else {
          setError("Report not found");
        }
      } catch (err: any) {
        console.error("Error fetching report:", err);
        setError(err?.message || "Failed to load report data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReportData();
  }, [id]);


  // Loading state
  if (isLoading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress size={60} sx={{ color: theme.colorTokens.P[70] }} />
        <Typography sx={styles.loadingText}>Loading report...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={styles.errorContainer}>
        <Typography sx={styles.errorTitle}>Error Loading Report</Typography>
        <Typography sx={styles.errorMessage}>{error}</Typography>
      </Box>
    );
  }

  // No data state
  if (!data) {
    return (
      <Box sx={styles.errorContainer}>
        <Typography sx={styles.errorTitle}>No Report Data</Typography>
        <Typography sx={styles.errorMessage}>
          Report data could not be loaded.
        </Typography>
      </Box>
    );
  }

  // Get company name from the first page that has it
  const companyName =
    data.pages.find((page) => "companyName" in page && page.companyName)
      ?.companyName || "Report";

  return (
    <Box sx={styles.wrapper}>
      {/* Generate PDF Button */}
      <GeneratePDF
        userName={companyName}
        type="services"
        setIsCreatingPDF={setIsCreatingPDF}
      />

      {/* Report Pages - Scaled for PDF generation */}
      <div id="content" className={classes.content}>
        <ScaledNewReportPages newData={data} />
      </div>
    </Box>
  );
};

export default NewReport;

const styles = {
  wrapper: {
    position: "relative" as "relative",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    gap: "20px",
  },
  loadingText: {
    fontSize: "16px",
    fontFamily: "var(--font-Regular)",
    color: "#666",
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column" as "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    gap: "12px",
    padding: "20px",
  },
  errorTitle: {
    fontSize: "24px",
    fontFamily: "var(--font-Bold)",
    color: "#d32f2f",
  },
  errorMessage: {
    fontSize: "16px",
    fontFamily: "var(--font-Regular)",
    color: "#666",
    textAlign: "center" as "center",
  },
};
